/* eslint-disable */
import React from 'react';
import AccordionTab, { AccordionTabProps } from './AccordionTab';

export const useTabs = (
  children: React.ReactElement<AccordionTabProps>,
): React.ReactElement<AccordionTabProps>[] => {
  const tabs: React.ReactElement<AccordionTabProps>[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === AccordionTab) {
        tabs.push(child);
        return;
      }

      throw new Error('Accordion children should be of type AccordionTab.');
    }
  });

  return tabs;
};

export const useTabEvents = (parent: HTMLDivElement) => {
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      /* eslint-disable-next-line no-console */
      if (!parent) console.warn('parent of AccordionTab should be an of type Accordion');

      const button = e.currentTarget;
      const activeTab =
        window.location.hash && parent?.querySelector(`#${window.location.hash.slice(1)}-title`);
      const isActive = activeTab === button;
      const content = parent?.querySelector<HTMLDivElement>(
        `#${button.getAttribute('aria-controls')}`,
      );

      if (!isActive) {
        window.location.hash = `#${content?.id}`;
        button.focus();
      }
    },
    [parent],
  );

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          e.currentTarget.click();
          break;

        case 'ArrowDown': {
          e.preventDefault();
          const nextButton =
            e.currentTarget.parentElement?.nextElementSibling?.nextElementSibling?.querySelector<HTMLButtonElement>(
              '.m-accordion__tab',
            );
          nextButton?.focus();
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          const previousButton =
            e.currentTarget.parentElement?.previousElementSibling?.previousElementSibling?.querySelector<HTMLButtonElement>(
              '.m-accordion__tab',
            );
          previousButton?.focus();
          break;
        }

        case 'Home': {
          e.preventDefault();
          const buttons = parent?.querySelectorAll<HTMLButtonElement>('.m-accordion__tab');
          if (!buttons?.length) break;

          buttons[0]?.focus();
          break;
        }

        case 'End': {
          e.preventDefault();
          const buttons = parent?.querySelectorAll<HTMLButtonElement>('.m-accordion__tab');
          if (!buttons?.length) break;

          buttons[buttons.length - 1]?.focus();
          break;
        }

        default:
          break;
      }
    },
    [parent],
  );

  return {
    handleClick,
    handleKeyPress,
  };
};

export const useAccordionEvents = (parent: HTMLDivElement | null) => {
  const openContent = React.useCallback((_element: HTMLDivElement) => {
    const element = _element;
    element.style.height = '0';
    element.style.overflow = 'hidden';
    element.removeAttribute('hidden');

    setTimeout(() => {
      element.style.height = `${element.scrollHeight}px`;
    });

    element.addEventListener(
      'transitionend',
      () => {
        element.style.height = '';
        element.style.overflow = '';
      },
      { once: true },
    );
  }, []);

  const closeContent = React.useCallback((element: HTMLDivElement | null) => {
    if (!element || element.getAttribute('hidden') === '') return;

    element.style.overflow = 'hidden';
    element.style.height = `${element.scrollHeight}px`;
    setTimeout(() => {
      element.style.height = '1px';
    });

    element.addEventListener(
      'transitionend',
      () => {
        element.style.height = '';
        element.style.overflow = '';
        element.setAttribute('hidden', '');
      },
      { once: true },
    );
  }, []);

  const handleHashChange = React.useCallback(() => {
    if (!parent || !window.location.hash) return;

    const content = parent.querySelector<HTMLDivElement>(`#${window.location.hash.slice(1)}`);
    let tabs: HTMLDivElement[] = Array.from(
      parent.querySelectorAll<HTMLDivElement>(`.m-accordion__tab`),
    );

    if (content && tabs) {
      const button = parent.querySelector(`#${content.id}-title`);
      tabs = tabs.filter((tab) => !tab.matches(`#${content.id}-title`));

      button?.setAttribute('aria-expanded', 'true');
      openContent(content!);
    }

    // close other tabs
    tabs?.forEach((tab) => {
      const tabContent = parent.querySelector<HTMLDivElement>(
        `#${tab.getAttribute('aria-controls')}`,
      );
      if (!tabContent) return;

      tab.setAttribute('aria-expanded', 'false');
      closeContent(tabContent);
    });
  }, [parent]);

  React.useEffect(() => {
    handleHashChange();
  }, [parent]);

  return {
    handleHashChange,
  };
};
